import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { fetchUserApps } from '../lib/user-apps-api';

// App keys that users can have access to
export type AppKey = 'admin' | 'iam' | 'training' | 'demo' | 'experimental' | 'finetuning';

interface AppPermissions {
  [key: string]: boolean;
}

interface UseAppPermissionsResult {
  permissions: AppPermissions;
  hasPermission: (appKey: AppKey) => boolean;
  allowedApps: AppKey[];
  isAdmin: boolean;
}

/**
 * Hook to check user's app permissions
 * For now, uses a simple check based on user role
 * TODO: Get permissions from IAM service API
 */
export function useAppPermissions(): UseAppPermissionsResult {
  const { user } = useAuth();

  // Fetch permissions from API
  const { data: userApps, isLoading } = useQuery({
    queryKey: ['user-apps', user?.id],
    queryFn: () => {
      if (!user?.id) return Promise.resolve([]);
      return fetchUserApps(user.id);
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const permissions = useMemo<AppPermissions>(() => {
    if (!user || isLoading) {
      return {} as AppPermissions;
    }

    // For admin/superadmin, grant all permissions by default
    const isAdmin = user.role === 'admin' || user.role === 'superadmin';

    if (isAdmin && !userApps) {
      // Admin users get all apps if API call fails/loading
      return {
        admin: true,
        iam: true,
        training: true,
        demo: true,
        experimental: true,
        finetuning: true,
      };
    }

    // Convert array to permissions object
    const perms: AppPermissions = {};
    (userApps || []).forEach((appKey: string) => {
      perms[appKey] = true;
    });

    return perms;
  }, [user, userApps, isLoading]);

  const hasPermission = (appKey: AppKey): boolean => {
    return permissions[appKey] === true;
  };

  const allowedApps = useMemo<AppKey[]>(() => {
    return Object.keys(permissions).filter(
      (key) => permissions[key] === true
    ) as AppKey[];
  }, [permissions]);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  return {
    permissions,
    hasPermission,
    allowedApps,
    isAdmin,
  };
}

export default useAppPermissions;
